import {
  curvatureLinearHandler,
  CURVATURE_LINEAR_RECONSTRUCTED_TYPE,
} from "./CurvatureLinearHandler";

/**
 * If `addFeatureHandlers` includes curvatureLinearHandler, injects a small
 * toggle button into the layer row's header letting the user disable the
 * client-side curvature line reconstruction without hiding the underlying
 * layer.
 *
 * Returns the (possibly wrapped) handlers array to pass to the layer/source
 * factory, and a `bindLayer(layer)` callback that must be called once the
 * layer exists — disabling removes already-reconstructed features from the
 * source, re-enabling re-runs the reconstruction over currently loaded ones.
 */
export function withCurvatureReconstructionToggle(
  addFeatureHandlers,
  headerEl,
) {
  if (!addFeatureHandlers.includes(curvatureLinearHandler)) {
    return { addFeatureHandlers, bindLayer: () => {} };
  }

  const toggleBtn = document.createElement("button");
  toggleBtn.type = "button";
  toggleBtn.className = "curvature-reconstruction-toggle-btn";
  toggleBtn.textContent = "∿";
  headerEl.appendChild(toggleBtn);

  let enabled = false;
  const updateTitle = () => {
    toggleBtn.title = enabled
      ? "Curvature line reconstruction is on — click to disable"
      : "Curvature line reconstruction is off — click to enable";
  };
  updateTitle();

  const wrappedHandlers = addFeatureHandlers.map((handler) =>
    handler === curvatureLinearHandler
      ? (event) => {
          if (enabled) handler(event);
        }
      : handler,
  );

  const bindLayer = (layer) => {
    toggleBtn.addEventListener("click", () => {
      enabled = !enabled;
      toggleBtn.classList.toggle(
        "curvature-reconstruction-toggle-btn--on",
        enabled,
      );
      updateTitle();

      const vectorSource = layer.getSource();
      if (enabled) {
        curvatureLinearHandler({ target: vectorSource });
      } else {
        vectorSource
          .getFeatures()
          .filter(
            (feature) =>
              feature.get("type") === CURVATURE_LINEAR_RECONSTRUCTED_TYPE,
          )
          .forEach((feature) => vectorSource.removeFeature(feature));
      }
    });
  };

  return { addFeatureHandlers: wrappedHandlers, bindLayer };
}
